import React, { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from 'reactstrap';
import Select from 'react-select';
import {
  ADVISORY_CONTACT_SCHEDULE_NAMES,
  ADVISORY_GOAL_NAMES,
  ADVISORY_MONTHLY_BUDGET_NAMES,
  ADVISORY_PROPERTY_TYPE_NAMES,
} from '../adapters/advisoryAdapters';
import { CreateAdvisoryLeadPayload } from '../services/dashboardService';
import { Option } from '../../../models/formModel';
import useProperList from '../../../hooks/useProperList';
import useSdrList from '../../../hooks/useSdrList';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  // Devuelve el mensaje de error del backend si la creación falló, o `null`
  // si fue exitosa — así el modal sabe si debe quedarse abierto (con los
  // datos ya cargados, para no hacer perder el trabajo) o cerrarse.
  onConfirm: (payload: CreateAdvisoryLeadPayload) => Promise<string | null>;
}

const toOptions = (names: Record<string, string>): Option[] =>
  Object.entries(names).map(([value, label]) => ({ value, label }));

const GOAL_OPTIONS = toOptions(ADVISORY_GOAL_NAMES);
const PROPERTY_TYPE_OPTIONS = toOptions(ADVISORY_PROPERTY_TYPE_NAMES);
const MONTHLY_BUDGET_OPTIONS = toOptions(ADVISORY_MONTHLY_BUDGET_NAMES);
const CONTACT_SCHEDULE_OPTIONS = toOptions(ADVISORY_CONTACT_SCHEDULE_NAMES);
const DELIVERY_TIME_OPTIONS: Option[] = [
  { value: 'inmediata', label: 'Inmediata' },
  { value: 'planos', label: 'Planos' },
];

const EMPTY_FORM = {
  first_name: '',
  last_name: '',
  phone: '',
  area_code: '',
  email: '',
  goal: '',
  property_type: '',
  delivery_time: '',
  zone: '',
  polygon_id: '',
  polygon_level: '',
  monthly_budget: '',
  contact_schedule: '',
  assigned_proper_id: '',
  assigned_sdr_id: '',
  date: '',
  time: '',
};

// La misma categoría que usa el backend para auto-asignar SDR en el form
// público (AdvisoryService.getNextAssignedSdrId) — acá se usa para filtrar
// el selector de SDR por rol: presale (planos) vs venta
// inmediata. No hay round-robin acá, el admin elige a mano.
const sdrRoleForDeliveryTime = (
  deliveryTime: string,
): 'SDR_SALES' | 'SDR_PRESALES' | null => {
  if (deliveryTime === 'inmediata') return 'SDR_SALES';
  if (deliveryTime === 'planos') return 'SDR_PRESALES';
  return null;
};

// Alta manual de un lead de Advisory — para cuando entra por otro canal
// (WhatsApp, llamada, etc.) y hay que cargarlo a mano. Mismos campos que el
// form público + datos de contacto (el lead no tiene sesión de customer),
// más proper/SDR/fecha: a diferencia del lead público, este nace ya
// aprobado y agendado (ver AdvisoryService.createApprovedLead) — no pasa
// por pending_approval ni por un approve() aparte.
const CreateAdvisoryLeadModal: React.FC<Props> = ({
  isOpen,
  toggle,
  onConfirm,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { propersOptions: properOptions, isLoadingPropers } = useProperList();

  const sdrRole = sdrRoleForDeliveryTime(form.delivery_time);
  const { sdrOptions, isLoadingSdrs } = useSdrList(sdrRole);

  const setField = (field: keyof typeof form) => (value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  // Cambiar el tiempo de entrega cambia el rol de SDR elegible (ver
  // sdrRoleForDeliveryTime) — el SDR ya elegido puede dejar de ser válido.
  const setDeliveryTime = (value: string) =>
    setForm((prev) => ({ ...prev, delivery_time: value, assigned_sdr_id: '' }));

  const isValid =
    form.first_name &&
    form.last_name &&
    form.phone &&
    form.area_code &&
    form.email &&
    form.goal &&
    form.property_type &&
    form.delivery_time &&
    form.zone &&
    form.monthly_budget &&
    form.contact_schedule &&
    form.assigned_proper_id &&
    form.assigned_sdr_id &&
    form.date &&
    form.time;

  const handleConfirm = async () => {
    if (!isValid || isSubmitting) return;
    setIsSubmitting(true);
    const { date, time, ...rest } = form;
    // El error (si lo hay) lo muestra el toast del padre — acá sólo importa
    // saber si hubo error para decidir si el modal se queda abierto (sin
    // perder lo ya tipeado) o se cierra.
    const error = await onConfirm({
      ...rest,
      polygon_id: form.polygon_id ? Number(form.polygon_id) : undefined,
      polygon_level: form.polygon_level ? Number(form.polygon_level) : undefined,
      scheduled_at: new Date(`${date}T${time}`).toISOString(),
    } as CreateAdvisoryLeadPayload);
    setIsSubmitting(false);
    if (error) return;
    setForm(EMPTY_FORM);
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} className="modal-dialog-centered" size="lg">
      <ModalHeader toggle={toggle}>Crear asesoría</ModalHeader>
      <ModalBody>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="create-advisory-first-name">Nombre</Label>
              <Input
                id="create-advisory-first-name"
                value={form.first_name}
                onChange={(e) => setField('first_name')(e.target.value)}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="create-advisory-last-name">Apellido</Label>
              <Input
                id="create-advisory-last-name"
                value={form.last_name}
                onChange={(e) => setField('last_name')(e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <FormGroup>
              <Label for="create-advisory-area-code">Código de área</Label>
              <Input
                id="create-advisory-area-code"
                placeholder="+503"
                value={form.area_code}
                onChange={(e) => setField('area_code')(e.target.value)}
              />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label for="create-advisory-phone">Teléfono</Label>
              <Input
                id="create-advisory-phone"
                value={form.phone}
                onChange={(e) => setField('phone')(e.target.value)}
              />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label for="create-advisory-email">Correo</Label>
              <Input
                id="create-advisory-email"
                type="email"
                value={form.email}
                onChange={(e) => setField('email')(e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label>Objetivo</Label>
              <Select
                options={GOAL_OPTIONS}
                value={GOAL_OPTIONS.find((o) => o.value === form.goal) ?? null}
                onChange={(option) => setField('goal')(option?.value ?? '')}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label>Tipo de propiedad</Label>
              <Select
                options={PROPERTY_TYPE_OPTIONS}
                value={
                  PROPERTY_TYPE_OPTIONS.find(
                    (o) => o.value === form.property_type,
                  ) ?? null
                }
                onChange={(option) =>
                  setField('property_type')(option?.value ?? '')
                }
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label>Tiempo de entrega</Label>
              <Select
                options={DELIVERY_TIME_OPTIONS}
                value={
                  DELIVERY_TIME_OPTIONS.find(
                    (o) => o.value === form.delivery_time,
                  ) ?? null
                }
                onChange={(option) => setDeliveryTime(option?.value ?? '')}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="create-advisory-zone">Zona de interés</Label>
              <Input
                id="create-advisory-zone"
                value={form.zone}
                onChange={(e) => setField('zone')(e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label>Salario mensual</Label>
              <Select
                options={MONTHLY_BUDGET_OPTIONS}
                value={
                  MONTHLY_BUDGET_OPTIONS.find(
                    (o) => o.value === form.monthly_budget,
                  ) ?? null
                }
                onChange={(option) =>
                  setField('monthly_budget')(option?.value ?? '')
                }
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label>Horario de contacto</Label>
              <Select
                options={CONTACT_SCHEDULE_OPTIONS}
                value={
                  CONTACT_SCHEDULE_OPTIONS.find(
                    (o) => o.value === form.contact_schedule,
                  ) ?? null
                }
                onChange={(option) =>
                  setField('contact_schedule')(option?.value ?? '')
                }
              />
            </FormGroup>
          </Col>
        </Row>

        <hr />

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="create-advisory-proper">Proper asignado</Label>
              <Select
                inputId="create-advisory-proper"
                options={properOptions}
                isLoading={isLoadingPropers}
                placeholder="Selecciona un proper"
                value={
                  properOptions.find((o) => o.value === form.assigned_proper_id) ?? null
                }
                onChange={(option) => setField('assigned_proper_id')(option?.value ?? '')}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="create-advisory-sdr">SDR asignado</Label>
              <Select
                inputId="create-advisory-sdr"
                options={sdrOptions}
                isLoading={isLoadingSdrs}
                isDisabled={!sdrRole}
                placeholder={
                  sdrRole ? 'Selecciona un SDR' : 'Elegí primero el tiempo de entrega'
                }
                value={sdrOptions.find((o) => o.value === form.assigned_sdr_id) ?? null}
                onChange={(option) => setField('assigned_sdr_id')(option?.value ?? '')}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label for="create-advisory-date">Fecha</Label>
              <Input
                type="date"
                id="create-advisory-date"
                value={form.date}
                onChange={(e) => setField('date')(e.target.value)}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="create-advisory-time">Hora</Label>
              <Input
                type="time"
                id="create-advisory-time"
                value={form.time}
                onChange={(e) => setField('time')(e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button
          color="primary"
          onClick={handleConfirm}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? 'Creando...' : 'Crear asesoría'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CreateAdvisoryLeadModal;
